import React, { useContext, useState } from 'react';
import {
  Button, Card, CardBody, CardTitle, Col, Row,
} from 'reactstrap';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArticleQuantity from '../../../components/ArticleQuantity';
import TextTruncate from '../../../components/TextTruncate';
import ToastSuccessMessage from '../../../components/ToastSuccessMessage';
import { OrderContext } from '../../../context/OrderContext';
import useMediaQuery from '../../../utils/hooks/mediaQuery.hook';
import { ARTICLE_IMAGE_SIZE, ARTICLE_IMAGE_SM_SIZE, ITEMS_LEFT_WARNING_COUNT } from '../constants';
import ImageCarousel from './ImageCarousel';
import SelectFeatures from './SelectFeatures';

import { ReactComponent as BasketSmallIcon } from '../../../assets/icons/basket-sm.svg';
import LinkWithQueryParams from '../../../components/LinkWithQueryParams';
import { CampaignContext } from '../../../context/CampaignWrapper';
import { Article } from '../../../generated/graphql';
import { isValidObjectId } from '../../../utils/helpers';

interface Props {
  variants: Article[]
  openArticleModal: (articleId: string) => void
  hideImages?: boolean
}

const ArticleCard = ({
  variants,
  openArticleModal,
  hideImages,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [variantIndex, setVariantIndex] = useState(0);
  const [translate] = useTranslation();
  const { slug } = useParams();
  const { isMobile } = useMediaQuery();
  const { order: { quantities }, setArticleQuantity } = useContext(OrderContext);
  const { campaign: { interfaceConfig: { hideArticleCounter } } } = useContext(CampaignContext);
  const {
    _id: articleId, name, description = '', price: p, imageUrls, itemsLeft,
  } = variants[variantIndex] || {};

  const { groupId } = variants[0] || {};

  const cardId = `card-${articleId}`;

  const price = Dinero({ amount: p || 0 }).toFormat();
  const isOutOfStock = itemsLeft === 0;
  const hasLowItemsLeft = !!itemsLeft && itemsLeft < ITEMS_LEFT_WARNING_COUNT;

  const hasHoverStyle = isHovered || isMobile;

  const imageSize = isMobile ? ARTICLE_IMAGE_SM_SIZE : ARTICLE_IMAGE_SIZE;
  const imageDimensions = { width: imageSize, height: imageSize };

  const quantitySectionStyle = `d-flex align-items-center ${hasHoverStyle ? '' : 'invisible'}`;
  const buttonStyle = `w-100 text-white ${hasHoverStyle ? '' : 'invisible'}`;
  const iconStyle = { verticalAlign: 'sub' };

  const buttonLabel = isOutOfStock
    ? translate('COMMON.OUT_OF_STOCK')
    : (
      <>
        {isMobile && <BasketSmallIcon style={iconStyle} />}
        {' '}
        <span>
          {translate('COMPONENTS.ARTICLE_CARD.ADD_BUTTON')}
        </span>
      </>
    );

  const descriptionSection = !!description && (
    <Col xs={12}>
      <TextTruncate text={description} maxLength={200} showExpandButton={false} />
    </Col>
  );

  const onMouseOver = () => setIsHovered(true);
  const onMouseLeave = () => setIsHovered(false);

  const onCardClick = () => {
    openArticleModal(articleId);
  };

  const addArticleToCart = () => {
    const currentQuantityInCart = quantities[articleId] || 0;
    const newQuantity = currentQuantityInCart + quantity;
    setArticleQuantity(articleId, newQuantity);

    const successMessage = (
      <ToastSuccessMessage message={translate('COMMON.ITEM_ADDED')}>
        <LinkWithQueryParams to={`/${slug}/checkout`} className="text-white text-decoration-underline">
          {translate('COMPONENTS.CART.CHECKOUT')}
          {' '}
          »
        </LinkWithQueryParams>
      </ToastSuccessMessage>
    );
    toast.success(successMessage, { toastId: articleId });
  };

  return (
    <Card
      className="article-card mx-auto"
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <CardBody id={cardId} className="p-0 p-md-3">
        <Row>
          {!hideImages && (
            <Col xs={5} md={12} className="pe-0 px-md-3">
              <div className="text-center mb-md-5">
                <ImageCarousel
                  onImageClick={onCardClick}
                  imageUrls={imageUrls}
                  imageSize={imageDimensions}
                  showNavigation={isHovered}
                  alt={name}
                />
              </div>
            </Col>
          )}
          <Col xs={hideImages ? 12 : 7} md={12} className="px-md-3">
            <div
              onClick={onCardClick}
              role="button"
              onKeyDown={(event) => {
                if (['keydown', 'keypress'].includes(event.type) && ['Enter', ' '].includes(event.key)) {
                  onCardClick();
                }
              }}
              tabIndex={0}
            >
              <CardTitle>
                <h6 className="m-0 fw-bold">{(isValidObjectId(groupId) ? name : groupId) || name}</h6>
              </CardTitle>
              {descriptionSection}
            </div>
            <SelectFeatures
              variantIndex={variantIndex}
              setVariantIndex={setVariantIndex}
              variants={variants}
              hide={!hasHoverStyle}
            />
            <div className="d-flex justify-content-between align-items-center flex-wrap text-center mb-3">
              <h5 className="mb-0">
                {price}
              </h5>
              {!isOutOfStock && variantIndex >= 0 && !hideArticleCounter && (
                <ArticleQuantity
                  quantity={quantity}
                  setQuantity={setQuantity}
                  className={quantitySectionStyle}
                />
              )}
            </div>
            {hasLowItemsLeft && (
              <p className="text-warning">
                {translate('COMPONENTS.ARTICLE_MODAL.ITEMS_AVAILABLE', {
                  count: itemsLeft,
                })}
              </p>
            )}
            <Button
              color="primary"
              className={buttonStyle}
              onClick={addArticleToCart}
              disabled={isOutOfStock || variantIndex < 0}
            >
              {buttonLabel}
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default ArticleCard;
